import React, { useEffect, useState } from 'react';
import QuestionnaireHeader from './components/QuestionnaireHeader';
import GenderSelection from './components/GenderSelection';
import ProgressBar from './components/ProgressBar';
import QuestionForm from './components/QuestionForm';
import ResultsDisplay from './components/ResultsDisplay';
import { questions, constitutions, determineConstitutionStatus } from './data/questionnaireData';
export function App() {
  const [gender, setGender] = useState<string>('female');
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [results, setResults] = useState<Record<string, {
    score: number;
    status: string;
  }>>({});
  const [showResults, setShowResults] = useState<boolean>(false);
  const [answeredCount, setAnsweredCount] = useState<number>(0);
  // Filter questions based on gender
  const filteredQuestions = questions;
  // Update answered count when answers change
  useEffect(() => {
    setAnsweredCount(Object.keys(answers).length);
  }, [answers]);
  // Handle gender change
  const handleGenderChange = (newGender: string) => {
    setGender(newGender);
    // Clear answers for gender-specific questions
    const newAnswers = {
      ...answers
    };
    if (newGender === 'female') {
      // Remove male-specific question answer (q60)
      delete newAnswers.q60;
    } else {
      // Remove female-specific question answer (q59)
      delete newAnswers.q59;
    }
    setAnswers(newAnswers);
  };
  // Handle answer change
  const handleAnswerChange = (questionNum: number, value: number) => {
    setAnswers(prev => ({
      ...prev,
      [`q${questionNum}`]: value
    }));
  };
  // Calculate scores for each constitution
  const calculateResults = () => {
    // Determine required questions count based on gender
    const requiredCount = gender === 'female' ? 59 : 59; // 59 for both genders (60 total minus the gender-specific question)
    if (answeredCount < requiredCount) {
      alert(`Please answer all questions. You have answered ${answeredCount} out of ${requiredCount} questions.`);
      return;
    }
    const calculatedResults: Record<string, {
      score: number;
      status: string;
    }> = {};
    // Calculate scores for each constitution
    Object.keys(constitutions).forEach(constitution => {
      const config = constitutions[constitution];
      let totalScore = 0;
      let itemCount = config.items.length;
      // Adjust item count for gender-specific questions
      if (gender === 'female' && config.items.includes(60)) {
        itemCount -= 1; // Reduce count for skipped male question
      } else if (gender === 'male' && config.items.includes(59)) {
        itemCount -= 1; // Reduce count for skipped female question
      }
      config.items.forEach(itemNum => {
        // Skip gender-specific questions based on gender
        if (gender === 'female' && itemNum === 60 || gender === 'male' && itemNum === 59) {
          return;
        }
        const answerValue = answers[`q${itemNum}`] || 1;
        if (config.reversed.includes(itemNum)) {
          // Reverse the score for reversed items
          const mapping: Record<number, number> = {
            1: 5,
            2: 4,
            3: 3,
            4: 2,
            5: 1
          };
          totalScore += mapping[answerValue];
        } else {
          totalScore += answerValue;
        }
      });
      // Calculate converted score using the formula
      const convertedScore = (totalScore - itemCount) / (itemCount * 4) * 100;
      const roundedScore = Math.round(convertedScore * 100) / 100;
      const status = determineConstitutionStatus(roundedScore, constitution);
      calculatedResults[constitution] = {
        score: roundedScore,
        status
      };
    });
    setResults(calculatedResults);
    setShowResults(true);
    // Scroll to results after a short delay
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  };
  return <div className="min-h-screen bg-[#f5f7fa] text-gray-800 py-8 px-4" data-id="element-0">
      <div className="max-w-3xl mx-auto bg-white p-8 rounded-xl shadow-md" data-id="element-1">
        <QuestionnaireHeader data-id="element-2" />
        <GenderSelection gender={gender} onGenderChange={handleGenderChange} data-id="element-3" />
        <ProgressBar completed={answeredCount} total={gender === 'female' ? 59 : 59} data-id="element-4" />
        <QuestionForm filteredQuestions={filteredQuestions} gender={gender} answers={answers} onAnswerChange={handleAnswerChange} data-id="element-5" />
        <button onClick={calculateResults} className="bg-gradient-to-r from-[#2c5530] to-[#4a7c59] text-white py-4 px-8 rounded-full text-lg font-semibold cursor-pointer block mx-auto mt-8 hover:transform hover:-translate-y-0.5 transition-all shadow-lg hover:shadow-xl" data-id="element-6">
          🧮 Calculate My TCM Constitution
        </button>
        <ResultsDisplay results={results} visible={showResults} data-id="element-7" />
      </div>
    </div>;
}